import { ReactNode, useState } from 'react';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold, LabelBold, LabelSmall } from 'styles/typography';
import CheckIcon from 'svg/CheckIcon';
import ChevronDown from 'svg/ChevronDown';

// The by-property surface's furniture (OLIO-3022): a collapsible section with a status chip, and
// a labeled field per property with a validity mark beside the control.
//
// The mark is PRESENCE-based and nothing more. A green check says the property has a value, not
// that the value is right — a botched capture (`Abigail,Smith` in First Name) marks valid, which
// is deliberate: the API carries no per-field validity, confidence or flag anywhere, so any
// stronger claim here would be invented by the browser.

export function FieldMark({ present }: { present: boolean }) {
  return present ? (
    <Valid data-testid='field-mark-valid' aria-label='Present'>
      <CheckIcon width={10} height={10} />
    </Valid>
  ) : (
    <Needed data-testid='field-mark-needed' aria-label='Needs attention'>
      !
    </Needed>
  );
}

export function SectionChip({ complete }: { complete: boolean }) {
  return complete ? (
    <CompleteChip data-testid='section-chip-complete'>Complete</CompleteChip>
  ) : (
    <NeededChip data-testid='section-chip-needed'>Action Needed</NeededChip>
  );
}

interface SectionProps {
  title: string;
  complete: boolean;
  testId: string;
  // The sentence under the header explaining what the person is reviewing here.
  description?: string;
  children: ReactNode;
}

export default function TaskSection({ title, complete, testId, description, children }: SectionProps) {
  // Sections needing work start open; finished ones start collapsed, as drawn. Initial state
  // only — collapsing a section under someone because their last keystroke completed it would
  // hide the answer they just gave.
  const [open, setOpen] = useState(!complete);

  return (
    <Section data-testid={testId}>
      <Header type='button' onClick={() => setOpen((prev) => !prev)} data-testid={`${testId}-toggle`}>
        <Caret $open={open}>
          <ChevronDown color={colors.black75} />
        </Caret>
        <BodySmallBold>{title}</BodySmallBold>
        <SectionChip complete={complete} />
      </Header>
      {open && (
        <>
          {description && <Description>{description}</Description>}
          <Rows>{children}</Rows>
        </>
      )}
    </Section>
  );
}

export function FieldRow({
  label,
  present,
  error,
  children,
}: {
  label: string;
  present: boolean;
  // Shown under the control while the mark reads "needs attention".
  error?: string;
  children: ReactNode;
}) {
  return (
    <Row data-testid={`task-field-${label}`}>
      {label && <RowLabel>{label}</RowLabel>}
      <ControlRow>
        <RowControl>{children}</RowControl>
        <FieldMark present={present} />
      </ControlRow>
      {!present && error && <FieldError>{error}</FieldError>}
    </Row>
  );
}

export function ReadOnlyValue({ value }: { value: string }) {
  return value ? <BodySmall>{value}</BodySmall> : <Missing>Not provided</Missing>;
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Header = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
`;

// Despite its name, the ChevronDown glyph is drawn pointing UP — so down is 180 and
// collapsed-right is 90, not 0 and -90.
const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transition: transform 0.15s;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '90deg')});
`;

const Description = styled(BodySmall)`
  color: ${colors.black75};
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RowLabel = styled(LabelSmall)`
  color: ${colors.black75};
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RowControl = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 320px;
`;

const FieldError = styled(LabelSmall)`
  color: ${colors.yellowHover};
`;

const Missing = styled(BodySmall)`
  color: ${colors.black50};
  font-style: italic;
`;

const Chip = styled(LabelBold).attrs({ as: 'span' })`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
`;

const CompleteChip = styled(Chip)`
  background: ${colors.green10};
  color: ${colors.greenExtraDark};
`;

const NeededChip = styled(Chip)`
  background: ${colors.yellow10};
  color: ${colors.yellowExtraDark};
`;

const Valid = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${colors.green100};
  color: ${colors.white};
`;

const Needed = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${colors.yellow100};
  color: ${colors.white};
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
`;
